//* Geometría de las gráficas de Métricas de TI (F-156). Funciones puras: el modelo de vista las
//* llama UNA vez al llegar los datos y la plantilla solo lee el resultado — el patrón que evita el
//* ciclo de detección de cambios de B-062.
//*
//* Coordenadas en porcentaje del área de trazado (0–100 en x y en y, y = 0 arriba). Las marcas se
//* dibujan en un SVG estirado (`preserveAspectRatio="none"`) y los textos y puntos van en HTML
//* encima, posicionados con `left/top` en %: así el texto no se deforma a ningún ancho.

export interface Pt {
  x: number
  y: number
}

/** Un texto de tooltip: título y renglones. */
export interface ChartTip {
  title: string
  lines: string[]
}

/** Todo lo que una gráfica dibuja, ya calculado. El componente no hace aritmética. */
export interface ChartModel {
  ariaLabel: string
  yTicks: { y: number; label: string }[]
  /** Etiquetas bajo cada posición (`S1`, `28`…). */
  xTicks: { x: number; label: string }[]
  /** Segundo nivel del eje (meses), centrado bajo sus posiciones. */
  xGroups: { x: number; label: string }[]
  paths: { d: string; kind: 'line' | 'area' | 'projected' | 'ideal' }[]
  vlines: { x: number; label: string; kind: 'divider' | 'today' }[]
  hlines: { y: number; label: string }[]
  bars: { x: number; y: number; w: number; h: number; kind: 'track' | 'progress' | 'reported' | 'resolved' }[]
  dots: { x: number; y: number; kind: 'solid' | 'hollow' | 'projected'; label: string | null; muted: boolean }[]
  /** Zonas de hover, una por posición, con su tooltip ya escrito. */
  slots: { x: number; w: number; tip: ChartTip }[]
}

const r2 = (n: number): number => Math.round(n * 100) / 100

/**
 * Máximo «redondo» del eje y sus marcas: pasos de 1·2·2.5·5 ×10ⁿ, para que las líneas guía caigan en
 * números que se leen sin pensar (0·100·200·300 en vez de 0·65·130·195).
 */
export function niceTicks(max: number, count = 4): { max: number; ticks: number[] } {
  if (!(max > 0)) return { max: 1, ticks: [0, 1] }
  const raw = max / count
  const pow = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow
  const top = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let v = 0; v <= top + step / 1000; v += step) ticks.push(r2(v))
  return { max: top, ticks }
}

/** Posición vertical (0 arriba) de un valor en un eje de 0 a `max`. */
export const scaleY = (v: number, max: number): number => r2(100 - (Math.max(0, v) / max) * 100)

/** Centro de la posición `i` de `n` repartidas a lo ancho (columnas y puntos por periodo). */
export const slotX = (i: number, n: number): number => r2(((i + 0.5) / n) * 100)

/** Posición `i` de `n` de borde a borde (series diarias). */
export const edgeX = (i: number, n: number): number => (n <= 1 ? 50 : r2((i / (n - 1)) * 100))

/** Polilínea recta. */
export function linePath(pts: Pt[]): string {
  return pts.map((p, i) => `${i ? 'L' : 'M'}${r2(p.x)},${r2(p.y)}`).join(' ')
}

/**
 * Curva suave y MONÓTONA entre puntos (Fritsch–Carlson): nunca se pasa por encima ni por debajo de
 * dos puntos consecutivos. Una curva común «inventa» picos que el dato no tiene.
 */
export function monotonePath(pts: Pt[]): string {
  const n = pts.length
  if (n < 3) return linePath(pts)
  const dx: number[] = []
  const m: number[] = []
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1].x - pts[i].x
    m[i] = dx[i] ? (pts[i + 1].y - pts[i].y) / dx[i] : 0
  }
  const t: number[] = new Array(n)
  t[0] = m[0]
  t[n - 1] = m[n - 2]
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) {
      t[i] = 0
      t[i + 1] = 0
      continue
    }
    const a = t[i] / m[i]
    const b = t[i + 1] / m[i]
    const s = a * a + b * b
    if (s > 9) {
      const tau = 3 / Math.sqrt(s)
      t[i] = tau * a * m[i]
      t[i + 1] = tau * b * m[i]
    }
  }
  let d = `M${r2(pts[0].x)},${r2(pts[0].y)}`
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3
    d += ` C${r2(pts[i].x + h)},${r2(pts[i].y + t[i] * h)} ${r2(pts[i + 1].x - h)},${r2(pts[i + 1].y - t[i + 1] * h)} ${r2(pts[i + 1].x)},${r2(pts[i + 1].y)}`
  }
  return d
}

/** Área bajo una línea (la misma curva, cerrada contra la base). */
export function areaPath(pts: Pt[], smooth = true): string {
  if (!pts.length) return ''
  const line = smooth ? monotonePath(pts) : linePath(pts)
  return `${line} L${r2(pts[pts.length - 1].x)},100 L${r2(pts[0].x)},100 Z`
}

/**
 * Agrupa posiciones consecutivas con la misma etiqueta (los meses del eje) y devuelve el centro de
 * cada grupo. `labels[i]` es la etiqueta de la posición `i`; `xs[i]`, su x.
 */
export function groupCenters(labels: string[], xs: number[]): { x: number; label: string }[] {
  const out: { x: number; label: string }[] = []
  let i = 0
  while (i < labels.length) {
    let j = i
    while (j + 1 < labels.length && labels[j + 1] === labels[i]) j++
    out.push({ x: r2((xs[i] + xs[j]) / 2), label: labels[i] })
    i = j + 1
  }
  return out
}

/** Porcentajes enteros que suman exactamente 100 (residuo mayor). */
export function percentages(values: number[]): number[] {
  const total = values.reduce((a, v) => a + v, 0)
  if (!total) return values.map(() => 0)
  const exact = values.map((v) => (v / total) * 100)
  const base = exact.map(Math.floor)
  let remainder = 100 - base.reduce((a, v) => a + v, 0)
  const order = exact.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((a, b) => b.frac - a.frac)
  for (const { i } of order) {
    if (remainder <= 0) break
    base[i]++
    remainder--
  }
  return base
}
